// src/components/padrekko/qc/builder/CaseBuilderMap.jsx
//
// Map-side of the case builder (mounted in MapPage's map area when
// mapMode==='padrekko_builder'). Owns:
//   - the draft-pads source/layers (squares render here; the pad being
//     vertex-edited is handed to MlDraw instead, so it isn't double-drawn)
//   - shift-drag lasso selection (a freehand polygon the user traces), tested
//     against the source layer's GeoJSON points (NOT queryRenderedFeatures, so
//     off-screen/decluttered points still select) → one buffered square per
//     point inside the outline
//   - the vertex-edit bridge: the selected pad is loaded into the shared MlDraw
//     instance (move/add/delete vertices), edits written back to the draft
//
// No DB writes happen here — everything lives in useCaseBuilderStore until
// Commit.

import { useEffect, useRef, useState } from "react";
import { useCaseBuilderStore } from "../../../../stores/useCaseBuilderStore";
import { useUserLayersStore } from "../../../../stores/useUserLayersStore";
import { useAppViewStore } from "../../../../stores/useAppViewStore";
import { squarePolygon, pointInRing, extractPoints, polygonBbox } from "../../../../lib/padBuffer";
import { toast } from "../../../../stores/useToastStore";
import CaseBuilderBanner from "./CaseBuilderBanner";

const SRC = "padrekko-draft-pads";
const FILL = "padrekko-draft-fill";
const LINE = "padrekko-draft-line";
const EMPTY = { type: "FeatureCollection", features: [] };

// Pure: build pad rows from the source layer's points inside a lasso ring (an
// array of [lon,lat], need not be explicitly closed). Reads everything from
// getState() so the drag handler closure never goes stale; returns null (and
// toasts) when there's nothing to select.
function buildPadsInPolygon(ring) {
  const st = useCaseBuilderStore.getState();
  if (!st.sourceLayerId) {
    toast.warning("Pick a source point layer first");
    return null;
  }
  const ul = useUserLayersStore.getState();
  const layer = [...(ul.layers || []), ...(ul.shared || [])].find((l) => l.id === st.sourceLayerId);
  // Test every extracted [lon,lat] (Point → one, MultiPoint → all) against the
  // traced outline, so a selected MultiPoint contributes one pad per coordinate.
  const hits = [];
  for (const f of layer?.geojson?.features || []) {
    // OGR/LIBKML emits the placemark name as capital-N `Name` for KMZ, so prefer
    // it; fall back to `name`/`pad_name`, then a default per extracted point.
    const base = f.properties?.Name || f.properties?.name || f.properties?.pad_name;
    const pts = extractPoints(f);
    pts.forEach(([lon, lat], j) => {
      if (!pointInRing([lon, lat], ring)) return;
      // Disambiguate multi-coordinate features so their pads aren't identical.
      const name = base ? (pts.length > 1 ? `${base} ${j + 1}` : base) : null;
      hits.push({ lon, lat, name });
    });
  }
  if (!hits.length) {
    toast.info("No points in that selection");
    return null;
  }
  const size = st.bufferMeters;
  return hits.map((h, i) => ({
    id: st.makePadId(),
    pad_name: String(h.name || `Pad ${i + 1}`),
    state_code: null,
    region: "pending",
    geometry: squarePolygon(h.lon, h.lat, size),
  }));
}

export default function CaseBuilderMap({ mapRef, mapReady }) {
  const [lasso, setLasso] = useState(null); // [{x,y}] pixel path while dragging

  // The shared MlDraw instance is published by MapPage to useAppViewStore. We
  // read it via getState() inside the edit effect (its mutable edit API is
  // driven directly); this boolean only gates the effect on availability.
  const drawReady = useAppViewStore((s) => !!s.drawInstance);

  const pads = useCaseBuilderStore((s) => s.pads);
  const highlightPadId = useCaseBuilderStore((s) => s.highlightPadId);
  const selectedPadId = useCaseBuilderStore((s) => s.selectedPadId);
  const focusedPadId = useCaseBuilderStore((s) => s.focusedPadId);
  const zoomNonce = useCaseBuilderStore((s) => s.zoomNonce);
  const sourceLayerId = useCaseBuilderStore((s) => s.sourceLayerId);

  // ── draft source + layers (added once, torn down on leave) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!map.getSource(SRC)) {
      map.addSource(SRC, { type: "geojson", data: EMPTY });
      map.addLayer({
        id: FILL,
        type: "fill",
        source: SRC,
        paint: {
          "fill-color": ["case", ["==", ["get", "bad"], 1], "#ef4444", "#38bdf8"],
          // Outline-only pads — no fill. Highlight/region cues come from the
          // LINE layer (width on hl, red on bad).
          "fill-opacity": 0,
        },
      });
      map.addLayer({
        id: LINE,
        type: "line",
        source: SRC,
        paint: {
          "line-color": ["case", ["==", ["get", "bad"], 1], "#ef4444", "#38bdf8"],
          "line-width": ["case", ["==", ["get", "hl"], 1], 3, 1.5],
        },
      });
    }
    return () => {
      if (!map || !map.style) return;
      if (map.getLayer(FILL)) map.removeLayer(FILL);
      if (map.getLayer(LINE)) map.removeLayer(LINE);
      if (map.getSource(SRC)) map.removeSource(SRC);
    };
  }, [mapRef, mapReady]);

  // ── push pad geometries into the draft source (exclude the edited pad) ──
  useEffect(() => {
    const map = mapRef.current;
    const src = map?.getSource?.(SRC);
    if (!src) return;
    const features = pads
      .filter((p) => p.id !== selectedPadId && p.geometry)
      .map((p) => ({
        type: "Feature",
        geometry: p.geometry,
        properties: {
          id: p.id,
          hl: p.id === highlightPadId || p.id === focusedPadId ? 1 : 0,
          bad: p.region && p.region !== "ok" && p.region !== "pending" ? 1 : 0,
        },
      }));
    src.setData({ type: "FeatureCollection", features });
  }, [pads, highlightPadId, focusedPadId, selectedPadId, mapRef, mapReady]);

  // ── lasso selection (shift-drag traces a freehand polygon) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    map.boxZoom.disable(); // shift-drag would otherwise box-zoom
    let path = null; // [{x,y}] pixel points accumulated while dragging
    const MIN_PX = 4;  // drop near-duplicate samples so the ring stays light

    const onDown = (e) => {
      if (!e.originalEvent.shiftKey || e.originalEvent.button !== 0) return;
      path = [{ x: e.point.x, y: e.point.y }];
      map.dragPan.disable();
      setLasso(path);
    };
    const onMove = (e) => {
      if (!path) return;
      const last = path[path.length - 1];
      if (Math.hypot(e.point.x - last.x, e.point.y - last.y) < MIN_PX) return;
      path = [...path, { x: e.point.x, y: e.point.y }];
      setLasso(path);
    };
    const onUp = () => {
      if (!path) return;
      const pts = path;
      path = null;
      setLasso(null);
      map.dragPan.enable();
      // A click / tiny drag isn't a lasso — need a real outline (≥ 3 points).
      if (pts.length < 3) return;
      // Unproject each screen sample to lng/lat so the ring tracks the drawn
      // shape (per-sample, so mercator distortion doesn't bow the edges).
      const ring = pts.map((p) => {
        const ll = map.unproject([p.x, p.y]);
        return [ll.lng, ll.lat];
      });
      const newPads = buildPadsInPolygon(ring);
      if (newPads) useCaseBuilderStore.getState().setPads(newPads);
    };

    map.on("mousedown", onDown);
    map.on("mousemove", onMove);
    map.on("mouseup", onUp);
    return () => {
      map.off("mousedown", onDown);
      map.off("mousemove", onMove);
      map.off("mouseup", onUp);
      if (map.style) { map.boxZoom.enable(); map.dragPan.enable(); }
    };
  }, [mapRef, mapReady]);

  // ── zoom-to the focused pad (zoomTo button + initial-load auto-focus) ──
  // Centralized here (not the panel) because this component owns the map handle
  // and its readiness; zoomNonce re-fires for a repeat click on the same pad.
  // Padding/maxZoom/duration match QcInspector.jsx's pad-zoom for a consistent
  // feel.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !focusedPadId) return;
    const pad = useCaseBuilderStore.getState().pads.find((p) => p.id === focusedPadId);
    const bb = pad && polygonBbox(pad.geometry);
    if (!bb) return;
    map.fitBounds(
      [[bb.west, bb.south], [bb.east, bb.north]],
      { padding: { top: 80, bottom: 80, left: 80, right: 80 }, maxZoom: 17, duration: 600 }
    );
  }, [focusedPadId, zoomNonce, mapRef, mapReady]);

  // ── initial load: select + zoom the first pad of a resumed draft (once) ──
  // Only fires when the builder opens with pads already present (crash-recovery
  // / deep-link resume). A fresh session that lassos pads is NOT auto-zoomed
  // — the user is already looking at the area they dragged.
  const didInitialFocus = useRef(false);
  useEffect(() => {
    if (didInitialFocus.current || !mapReady) return;
    const st = useCaseBuilderStore.getState();
    if (!st.pads.length) return;
    didInitialFocus.current = true;
    st.focusPad(st.pads[0].id);
  }, [mapReady]);

  // ── zoom to the source layer's point extent when it's (re)picked ──
  // Skips the first actionable run so a resumed draft's pad-focus zoom (above)
  // isn't overridden by the persisted sourceLayerId; fires when the user picks
  // a layer afterward, or when a freshly imported layer auto-selects.
  const didMountSource = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!didMountSource.current) { didMountSource.current = true; return; }
    if (!sourceLayerId) return;
    const ul = useUserLayersStore.getState();
    const layer = [...(ul.layers || []), ...(ul.shared || [])].find((l) => l.id === sourceLayerId);
    let north = -Infinity, south = Infinity, east = -Infinity, west = Infinity;
    for (const f of layer?.geojson?.features || []) {
      for (const [lon, lat] of extractPoints(f)) {
        if (lat > north) north = lat;
        if (lat < south) south = lat;
        if (lon > east) east = lon;
        if (lon < west) west = lon;
      }
    }
    if (!Number.isFinite(north)) return; // no points (yet) — nothing to fit
    map.fitBounds(
      [[west, south], [east, north]],
      { padding: { top: 80, bottom: 80, left: 80, right: 80 }, maxZoom: 16, duration: 600 }
    );
  }, [sourceLayerId, mapReady, mapRef]);

  // ── vertex-edit bridge: hand the selected pad to MlDraw, write edits back ──
  useEffect(() => {
    const draw = useAppViewStore.getState().drawInstance;
    if (!draw || !mapReady) return;
    const pad = useCaseBuilderStore.getState().pads.find((p) => p.id === selectedPadId);
    if (!selectedPadId || !pad) {
      draw.exitVertexEdit?.();
      draw.delete?.();
      return undefined;
    }
    const feature = {
      type: "Feature",
      geometry: JSON.parse(JSON.stringify(pad.geometry)),
      properties: {},
    };
    draw.add(feature);
    draw.enterVertexEdit?.();

    const prevOnUpdate = draw.onUpdate;
    draw.onUpdate = () => {
      prevOnUpdate?.();
      const f = draw.lastFeature;
      if (f?.geometry) {
        useCaseBuilderStore
          .getState()
          .replacePadGeometry(selectedPadId, JSON.parse(JSON.stringify(f.geometry)));
      }
    };
    return () => {
      draw.onUpdate = prevOnUpdate;
      draw.exitVertexEdit?.();
      draw.delete?.();
    };
  }, [selectedPadId, drawReady, mapReady]);

  return (
    <>
      <CaseBuilderBanner />
      {lasso && lasso.length > 1 && (
        <svg className="absolute inset-0 z-20 w-full h-full pointer-events-none">
          <polygon
            points={lasso.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="rgba(22,242,212,0.12)"
            stroke="#16f2d4"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </>
  );
}
